import { useEffect, useState } from 'react'

import { DemoBadge } from '../learning/DemoBadge'
import { JourneyFrame, Note, SourceChip } from './JourneyChrome'
import {
  type LoadSampleMaterials,
  type SampleMaterial,
  type SourceLine,
  type Topic,
} from './model'
import { DEMO_TEXT, PANEL, PRIMARY, SECONDARY, subjectOf } from './ui'

/**
 * Steps 1–3 for a DEMO sample: choose a sample, review the original page next
 * to the (simulated) extracted content, and pick the topic to learn. The three
 * kinds of content stay visibly separate: original source, extracted content
 * and, later, explanation and practice.
 */

type SamplesState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; samples: readonly SampleMaterial[] }

export function SamplePicker({
  load,
  onChoose,
  onBack,
  onHome,
}: {
  load: LoadSampleMaterials
  onChoose: (sample: SampleMaterial) => void
  onBack: () => void
  onHome: () => void
}): React.JSX.Element {
  const [state, setState] = useState<SamplesState>({ kind: 'loading' })
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let active = true
    load().then(
      (samples) => {
        if (active) setState({ kind: 'ready', samples })
      },
      () => {
        if (active) setState({ kind: 'error' })
      },
    )
    return () => {
      active = false
    }
  }, [load, reloadKey])

  return (
    <JourneyFrame
      step="Tài liệu"
      title="Chọn tài liệu mẫu"
      onBack={onBack}
      onHome={onHome}
      demo
    >
      <Note>{DEMO_TEXT.source}</Note>

      {state.kind === 'loading' && (
        <p role="status" className="text-lg text-slate-700 dark:text-slate-300">
          Đang tải tài liệu mẫu…
        </p>
      )}
      {state.kind === 'error' && (
        <div className="flex flex-col items-start gap-3">
          <p role="alert" className="text-lg">
            Chưa tải được tài liệu mẫu. Con thử lại nhé.
          </p>
          <button
            type="button"
            className={SECONDARY}
            onClick={() => {
              setState({ kind: 'loading' })
              setReloadKey((k) => k + 1)
            }}
          >
            Thử lại
          </button>
        </div>
      )}
      {state.kind === 'ready' && state.samples.length === 0 && (
        <p className="text-lg">Hiện chưa có tài liệu mẫu nào.</p>
      )}
      {state.kind === 'ready' && state.samples.length > 0 && (
        <ul aria-label="Tài liệu mẫu" className="grid gap-3 md:grid-cols-2">
          {state.samples.map((sample) => {
            const subject = subjectOf(sample)
            return (
              <li key={sample.id}>
                <button
                  type="button"
                  className="flex h-full w-full items-center gap-4 rounded-3xl border-2 border-slate-200 bg-white p-4 text-left active:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:active:bg-slate-800"
                  onClick={() => {
                    onChoose(sample)
                  }}
                >
                  <span className="text-4xl" aria-hidden>
                    {subject?.icon ?? '📄'}
                  </span>
                  <span className="flex min-w-0 flex-col gap-1">
                    <span className="text-base font-semibold text-sky-800 dark:text-sky-300">
                      {subject?.name}
                    </span>
                    <span className="text-lg font-semibold">{sample.title}</span>
                    <span className="text-base text-slate-600 dark:text-slate-400">
                      {sample.sourceRef}
                    </span>
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </JourneyFrame>
  )
}

/**
 * A printed line; an `unclear` part is drawn smudged, as on a blurred photo,
 * so the uncertainty flagged in the extracted content is visible at its source.
 */
function SourceLineText({ line }: { line: SourceLine }): React.JSX.Element {
  const at = line.unclear === undefined ? -1 : line.text.indexOf(line.unclear)
  if (line.unclear === undefined || at === -1) return <p>{line.text}</p>
  return (
    <p>
      {line.text.slice(0, at)}
      <span
        aria-hidden
        data-testid="source-unclear"
        className="inline-block blur-[2.5px] select-none"
      >
        {line.unclear}
      </span>
      <span className="sr-only">(chỗ này bị mờ, khó đọc)</span>
      {line.text.slice(at + line.unclear.length)}
    </p>
  )
}

/** The sample page, drawn like paper so it reads as "the original". */
function SourcePage({ sample }: { sample: SampleMaterial }): React.JSX.Element {
  return (
    <section aria-labelledby="source-original" className={PANEL}>
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="source-original" className="text-lg font-semibold">
          <span aria-hidden>📄 </span>Tài liệu gốc
        </h2>
        <DemoBadge />
      </div>
      <div
        lang={sample.subject === 'english' ? 'en' : undefined}
        className="flex flex-col gap-2 rounded-xl border border-amber-200 bg-amber-50/60 p-4 font-serif text-lg leading-relaxed text-slate-900 shadow-inner dark:border-amber-900 dark:bg-amber-950/30 dark:text-slate-100"
      >
        <p className="text-right font-sans text-sm text-slate-600 dark:text-slate-400">
          {sample.sourceRef}
        </p>
        {sample.source.map((line) => (
          <SourceLineText key={line.id} line={line} />
        ))}
      </div>
      <p className="text-base text-slate-600 dark:text-slate-400">{DEMO_TEXT.source}</p>
    </section>
  )
}

export function ContentReview({
  sample,
  onContinue,
  onReturn,
  onBack,
  onHome,
}: {
  sample: SampleMaterial
  onContinue: () => void
  /** Opened from a later step to look at the source again: go back there. */
  onReturn: (() => void) | null
  onBack: () => void
  onHome: () => void
}): React.JSX.Element {
  const uncertain = sample.extracted.filter((b) => b.uncertainNote !== null)
  return (
    <JourneyFrame
      step="Nội dung"
      title="Xem lại nội dung"
      onBack={onReturn ?? onBack}
      onHome={onHome}
      demo
    >
      <p className="text-lg text-slate-700 dark:text-slate-300">
        So sánh tài liệu gốc với nội dung ứng dụng đọc được.
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <SourcePage sample={sample} />
        <section aria-labelledby="source-extracted" className={PANEL}>
          <div className="flex flex-wrap items-center gap-2">
            <h2 id="source-extracted" className="text-lg font-semibold">
              <span aria-hidden>🔎 </span>Nội dung đọc được
            </h2>
            <DemoBadge />
          </div>
          <ul className="flex flex-col gap-2">
            {sample.extracted.map((block) => (
              <li
                key={block.id}
                className={`rounded-xl p-3 ${
                  block.uncertainNote === null
                    ? 'bg-slate-50 dark:bg-slate-800'
                    : 'border-2 border-amber-400 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40'
                }`}
              >
                <p className="text-sm font-semibold text-slate-600 dark:text-slate-400">
                  {block.label}
                </p>
                <p
                  lang={sample.subject === 'english' ? 'en' : undefined}
                  className="text-lg"
                >
                  {block.text}
                </p>
                {block.uncertainNote !== null && (
                  <p className="mt-1 text-base font-semibold text-amber-900 dark:text-amber-200">
                    <span aria-hidden>⚠️ </span>Chưa chắc chắn: {block.uncertainNote}
                  </p>
                )}
              </li>
            ))}
          </ul>
          <Note>{DEMO_TEXT.extraction}</Note>
        </section>
      </div>
      {uncertain.length > 0 && (
        <p className="text-base text-slate-700 dark:text-slate-300">
          Có {uncertain.length} chỗ chưa chắc chắn. Nếu khác tài liệu gốc, con hỏi bố mẹ
          hoặc chọn tài liệu khác nhé.
        </p>
      )}
      {onReturn === null ? (
        <div className="flex flex-col gap-3 md:flex-row">
          <button type="button" className={PRIMARY} onClick={onContinue}>
            Nội dung đúng, tiếp tục
          </button>
          <button type="button" className={SECONDARY} onClick={onBack}>
            Chọn tài liệu khác
          </button>
        </div>
      ) : (
        <button type="button" className={PRIMARY} onClick={onReturn}>
          Quay lại bài đang học
        </button>
      )}
    </JourneyFrame>
  )
}

export function TopicSelect({
  sample,
  onChoose,
  onOpenSource,
  onBack,
  onHome,
}: {
  sample: SampleMaterial
  onChoose: (topic: Topic) => void
  onOpenSource: () => void
  onBack: () => void
  onHome: () => void
}): React.JSX.Element {
  const [selectedId, setSelectedId] = useState(sample.topics[0]?.id ?? '')
  const selected = sample.topics.find((t) => t.id === selectedId)
  const several = sample.topics.length > 1

  return (
    <JourneyFrame
      step="Chủ đề"
      title="Chủ đề để học"
      onBack={onBack}
      onHome={onHome}
      demo
    >
      <SourceChip sample={sample} onOpen={onOpenSource} />
      <fieldset className="flex min-w-0 flex-col gap-3">
        <legend className="mb-3 text-lg text-slate-700 dark:text-slate-300">
          {several
            ? 'Tài liệu này có các chủ đề sau. Con chọn một nhé.'
            : 'Chủ đề tìm thấy trong tài liệu:'}
        </legend>
        {sample.topics.map((topic) => {
          const isSelected = topic.id === selectedId
          return (
            <label
              key={topic.id}
              className={`flex cursor-pointer flex-col gap-2 rounded-3xl border-2 p-4 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-sky-600 ${
                isSelected
                  ? 'border-sky-700 bg-sky-50 dark:border-sky-400 dark:bg-sky-950'
                  : 'border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-900'
              }`}
            >
              <span className="flex items-center gap-3">
                <input
                  type="radio"
                  name="topic"
                  value={topic.id}
                  checked={isSelected}
                  onChange={() => {
                    setSelectedId(topic.id)
                  }}
                  className="size-5 accent-sky-700"
                />
                <span className="text-xl font-semibold">{topic.title}</span>
              </span>
              <span className="text-base">
                <span className="font-semibold">Mục tiêu: </span>
                {topic.objective}
              </span>
              <span className="text-base text-slate-700 dark:text-slate-300">
                <span className="font-semibold">Học xong con sẽ: </span>
                {topic.canDo.join(' · ')}
              </span>
              <span className="text-sm text-slate-600 dark:text-slate-400">
                Dựa trên:{' '}
                {topic.blockIds
                  .map((id) => sample.extracted.find((b) => b.id === id)?.label ?? id)
                  .join(', ')}{' '}
                · {topic.questions.length} câu luyện tập
              </span>
            </label>
          )
        })}
      </fieldset>
      <button
        type="button"
        className={PRIMARY}
        disabled={selected === undefined}
        onClick={() => {
          if (selected !== undefined) onChoose(selected)
        }}
      >
        Học chủ đề này
      </button>
    </JourneyFrame>
  )
}
